import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { API, RATING_META, CATEGORY_LABELS, inputCls, labelCls } from './groupMeta';

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const Stars = ({ value, onChange, testId }) => (
  <div className="flex gap-0.5" data-testid={testId}>
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange(value === n ? 0 : n)} data-testid={`${testId}-${n}`}>
        <Star className={`w-4 h-4 transition-colors ${n <= value ? 'fill-amber-300 text-amber-300' : 'text-white/20 hover:text-white/40'}`} />
      </button>
    ))}
  </div>
);

export const InspectionFormModal = ({ open, group, canShutdown, onClose, onSaved, getAuthHeaders }) => {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ date: today(), rating: '', scores: {}, summary: '', strengths: '', improvements: '', notes: '', next_inspection_due: plusDays(60) }); }, [open]);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.rating) return toast.error('Choose an overall rating');
    if (f.rating === 'shutdown' && !window.confirm(`Rating "Shutdown" will archive and lock ${group.name}. Continue?`)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/groups/${group.id}/inspections`, { ...f, next_inspection_due: f.next_inspection_due || null }, { headers: getAuthHeaders() });
      toast.success('Inspection recorded');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Save failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0b0b16] border-white/[0.08] text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inspection-form-modal">
        <DialogHeader><DialogTitle>New inspection · {group?.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Inspection date</label><input type="date" value={f.date || ''} onChange={(e) => set('date', e.target.value)} className={inputCls} data-testid="inspection-date" /></div>
          <div><label className={labelCls}>Next inspection due</label><input type="date" value={f.next_inspection_due || ''} onChange={(e) => set('next_inspection_due', e.target.value)} className={inputCls} data-testid="inspection-next-due" /></div>
        </div>
        <div>
          <label className={labelCls}>Overall rating *</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {Object.entries(RATING_META).filter(([k]) => k !== 'shutdown' || canShutdown).map(([k, m]) => (
              <button key={k} type="button" onClick={() => set('rating', k)} data-testid={`rating-option-${k}`}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-[background-color,border-color]"
                style={f.rating === k ? { background: `${m.hex}22`, borderColor: m.hex, color: m.hex } : { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.55)' }}>
                <m.Icon className="w-5 h-5" /><span className="text-[10px] font-semibold leading-tight">{m.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
          <p className="sm:col-span-2 text-[9px] tracking-[0.18em] uppercase text-white/45 font-semibold">Category scores (optional)</p>
          {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
            <div key={k} className="flex items-center justify-between">
              <span className="text-xs text-white/65">{l}</span>
              <Stars value={f.scores?.[k] || 0} onChange={(v) => set('scores', { ...f.scores, [k]: v })} testId={`score-${k}`} />
            </div>
          ))}
        </div>
        {[['summary', 'Summary', 2], ['strengths', 'Strengths', 2], ['improvements', 'Areas for improvement', 2], ['notes', 'Inspector notes', 4]].map(([k, l, rows]) => (
          <div key={k}><label className={labelCls}>{l}</label>
            <textarea rows={rows} value={f[k] || ''} onChange={(e) => set(k, e.target.value)} className={`${inputCls} resize-none`} data-testid={`inspection-${k}`} /></div>
        ))}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-xs text-white/50 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="inspection-save-btn"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-semibold text-[#0a0a14] bg-gradient-to-r from-cyan-400 to-violet-400 disabled:opacity-50">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Record inspection
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
